import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet";
import * as api from "../api";
import { formatPhone, sendWa, waMessage, generateReceiptPDF, sendReceiptViaWhatsApp, setReceiptSizesCache, setBrandingCache, shouldAskWaConsent, markWaSent, markWaDeclined } from "../utils";
import { C, R, F, statusStyle, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Badge, Ref, Chip, Callout, Spinner, EmptyState, Stat, Modal, Input, Textarea } from "./UI";
import { DraggableMarker } from "./Map";

// Cible tactile minimale — écrans utilisés d'une main, en déplacement.
const TAP = { minHeight: 44 };

function ColList({ setView, setCurrentOrder, user }) {
  const [orders,     setOrders]     = useState([]);
  const [tab,        setTab]        = useState("pending");
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [search,     setSearch]     = useState("");

  // Modal nouvelle commande
  const [showForm,   setShowForm]   = useState(false);
  const [fClient,    setFClient]    = useState("");
  const [fPhone,     setFPhone]     = useState("");
  const [fAddress,   setFAddress]   = useState("");
  const [fDate,      setFDate]      = useState("");
  const [fTime,      setFTime]      = useState("");
  const [fNotes,     setFNotes]     = useState("");
  const [fSaving,    setFSaving]    = useState(false);
  const [fError,     setFError]     = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [newOrder,   setNewOrder]   = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [editAddrOrder, setEditAddrOrder] = useState(null);
  const [editAddrValue, setEditAddrValue] = useState("");
  const [editAddrLoading, setEditAddrLoading] = useState(false);
  const [editAddrLat, setEditAddrLat] = useState(null);
  const [editAddrLng, setEditAddrLng] = useState(null);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [myAssignments, setMyAssignments] = useState(null);
  const [printSizeModal, setPrintSizeModal] = useState(null);
  const [waModal, setWaModal] = useState(null); // { order, message }
  const gpsWatchRef = useRef(null);
  const gpsIntervalRef = useRef(null);
  const lastPosRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  const load = useCallback(async () => {
    setLoading(true);
    try { setOrders(await api.getOrders()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.getMyAssignments().then(d => setMyAssignments(d)).catch(() => {});
    api.getSettings().then(s => {
      if (s && s.receipt_font_sizes) {
        try { setReceiptSizesCache(JSON.parse(s.receipt_font_sizes)); } catch {}
      }
      if (s) setBrandingCache({ phone_main: s.phone_main, phone_whatsapp: s.phone_whatsapp });
    }).catch(() => {});
  }, [load]);

  function openForm() {
    setFClient(""); setFPhone(""); setFAddress("");
    setFDate(today); setFTime(""); setFNotes("");
    setFError(""); setNewOrder(null); setClientInfo(null); setShowForm(true);
  }

  async function lookupClient() {
    const p = fPhone.replace(/\s/g, "");
    if (p.length >= 9) {
      try {
        const info = await api.searchClient(p);
        if (info && info.client) {
          setClientInfo(info);
          if (!fClient && info.client) setFClient(info.client);
          if (!fAddress && info.address) setFAddress(info.address);
        }
      } catch { /* no client */ }
    }
  }

  function getGPS() {
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`, { headers: { "User-Agent": "SetalExpress/1.0" } });
          const d = await r.json();
          const a = d.address || {};
          const parts = [a.house_number, a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village].filter(Boolean);
          setFAddress(parts.length >= 2 ? parts.join(", ") : d.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        } catch { setFAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`); }
        setGpsLoading(false);
      },
      () => { setFError("Impossible d'obtenir la position GPS"); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }

  async function handleCreateOrder(e) {
    e.preventDefault();
    if (!fClient || !fPhone || !fAddress || !fDate || !fTime) {
      setFError("Veuillez remplir tous les champs obligatoires."); return;
    }
    setFSaving(true); setFError("");
    try {
      const created = await api.createOrder({ client: fClient, phone: fPhone, address: fAddress, date: fDate, time: fTime, notes: fNotes });
      setNewOrder(created);
      load();
    } catch (err) { setFError(err.message); }
    finally { setFSaving(false); }
  }

  // Cleanup GPS tracking on unmount
  useEffect(() => {
    return () => {
      if (gpsWatchRef.current !== null) navigator.geolocation.clearWatch(gpsWatchRef.current);
      if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
    };
  }, []);

  function startGPSTracking(orderId) {
    setTrackingOrderId(orderId);
    // Watch GPS position
    if ("geolocation" in navigator) {
      gpsWatchRef.current = navigator.geolocation.watchPosition(
        pos => {
          lastPosRef.current = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            heading: pos.coords.heading,
            speed: pos.coords.speed,
            accuracy: pos.coords.accuracy,
          };
        },
        () => { /* GPS error, ignore silently */ },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );

      // Send position to server every 10 seconds
      gpsIntervalRef.current = setInterval(() => {
        if (lastPosRef.current) {
          api.updateCollectorLocation({
            order_id: orderId,
            ...lastPosRef.current,
          }).catch(() => {});
        }
      }, 10000);

      // Send first position immediately
      navigator.geolocation.getCurrentPosition(pos => {
        api.updateCollectorLocation({
          order_id: orderId,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          accuracy: pos.coords.accuracy,
        }).catch(() => {});
      }, () => {}, { enableHighAccuracy: true });
    }
  }

  function stopGPSTracking() {
    if (gpsWatchRef.current !== null) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
    if (gpsIntervalRef.current) {
      clearInterval(gpsIntervalRef.current);
      gpsIntervalRef.current = null;
    }
    setTrackingOrderId(null);
    api.stopCollectorTracking().catch(() => {});
  }

  async function goEnRoute(order) {
    try {
      await api.updateOrder(order.id, { collector_status: "en_route" });
      startGPSTracking(order.id);
      load();
    } catch (err) { alert(err.message); }
  }

  async function confirmCollect(order) {
    try {
      await api.updateOrder(order.id, { collector_status: "arrived" });
      load();
    } catch (err) { alert(err.message); }
  }

  async function cancelEnRoute(order) {
    if (!window.confirm(`Annuler le départ pour ${order.id} ?\nLa commande repasse en attente de collecteur.`)) return;
    try {
      stopGPSTracking();
      await api.updateOrder(order.id, { collector_status: null });
      load();
    } catch (err) { alert(err.message); }
  }

  async function optimizeRoute() {
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          reject, { enableHighAccuracy: true, timeout: 15000 }
        );
      });

      let stops = orders.filter(o => o.status === "En attente" && !o.collector_status && o.latitude && o.longitude)
        .map(o => ({ id: o.id, lat: Number(o.latitude), lng: Number(o.longitude), client: o.client, address: o.address }));

      if (stops.length < 2) { alert("Il faut au moins 2 commandes avec coordonnées GPS"); return; }

      // Nearest neighbor algorithm
      const ordered = [];
      let current = pos;
      while (stops.length > 0) {
        let nearest = 0;
        let minDist = Infinity;
        stops.forEach((s, i) => {
          const d = Math.sqrt(Math.pow(s.lat - current.lat, 2) + Math.pow(s.lng - current.lng, 2));
          if (d < minDist) { minDist = d; nearest = i; }
        });
        ordered.push(stops[nearest]);
        current = stops[nearest];
        stops.splice(nearest, 1);
      }

      setOptimizedRoute({ origin: pos, stops: ordered });
    } catch (err) {
      alert("Impossible d'obtenir la position GPS : " + (err.message || err));
    }
  }

  function openOptimizedMaps() {
    if (!optimizedRoute) return;
    const { origin, stops } = optimizedRoute;
    const originStr = `${origin.lat},${origin.lng}`;
    const destination = `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
    const waypoints = stops.slice(0, -1).map(s => `${s.lat},${s.lng}`).join("|");
    const url = `https://www.google.com/maps/dir/?api=1&origin=${originStr}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    window.open(url, "_blank");
  }

  // Exclure les commandes sur place — le livreur ne gère que les collectes/livraisons
  const deliveryOrders = orders.filter(o => o.order_type !== "sur_place");

  const pending   = deliveryOrders.filter(o => o.status === "En attente");
  const enRoute   = deliveryOrders.filter(o => o.status === "En attente" && o.collector_status === "en_route");
  const arrived   = deliveryOrders.filter(o => o.status === "En attente" && o.collector_status === "arrived");
  const collected = deliveryOrders.filter(o => o.status === "Collecté" || (o.status === "En attente" && o.collector_status === "arrived"));
  const readyForDelivery = deliveryOrders.filter(o => o.status === "Prêt");

  // Mes collectes : commandes déjà collectées/traitées par ce livreur
  const myCollections = deliveryOrders.filter(o =>
    o.collector_id === user?.id && ["Collecté", "En traitement", "Prêt", "Livré"].includes(o.status)
  );

  // Planning: today's orders sorted by time (hors sur_place)
  const todayStr = new Date().toISOString().split("T")[0];
  const todayOrders = deliveryOrders.filter(o => o.date === todayStr).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const getDisplayed = () => {
    let list = tab === "pending" ? pending.filter(o => o.collector_status !== "en_route") : tab === "enroute" ? enRoute : tab === "collected" ? collected : tab === "delivery" ? readyForDelivery : tab === "mycol" ? myCollections : todayOrders;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o => o.id.toLowerCase().includes(q) || o.client.toLowerCase().includes(q) || o.phone.includes(q));
    }
    return list;
  };
  const displayed = getDisplayed();

  const TABS = [
    { k: "pending",   icon: "pending",  label: "Demandes",      count: pending.length },
    { k: "enroute",   icon: "collect",  label: "En route",      count: enRoute.length },
    { k: "collected", icon: "package",  label: "Sur place",     count: collected.length },
    { k: "delivery",  icon: "delivery", label: "Livraison",     count: readyForDelivery.length },
    { k: "mycol",     icon: "check",    label: "Mes collectes", count: myCollections.length },
    { k: "planning",  icon: "calendar", label: "Planning",      count: todayOrders.length },
  ];

  const emptyLabel = tab === "pending" ? "Aucune demande en attente"
    : tab === "enroute" ? "Aucun collecteur en route"
    : tab === "delivery" ? "Aucune commande à livrer"
    : tab === "collected" ? "Aucune commande en cours"
    : tab === "mycol" ? "Aucune collecte effectuée"
    : "Rien au planning aujourd'hui";

  // Bandeau « GPS actif » — réutilisé par le planning et la liste
  const gpsBanner = (text) => (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
      background: C.successSoft, border: `1px solid ${C.successBorder}`, borderRadius: R.md,
      padding: "8px 11px", fontSize: F.size.base, color: C.success, fontWeight: F.weight.medium,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.success, flexShrink: 0, animation: "pulse 1.6s ease infinite" }} />
      <Icon name="track" size={14} />
      {text}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, color: C.inkOn, padding: "14px 16px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: F.size.sm, color: "rgba(255,255,255,.72)" }}>Espace collecteur</p>
            <h1 className="truncate" style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.01em" }}>
              {user?.name}
            </h1>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <Btn variant="white" icon="add" onClick={openForm} style={TAP}>Commande</Btn>
            <Btn
              variant="ghost" icon="logout" title="Déconnexion"
              style={{ ...TAP, borderColor: "rgba(255,255,255,.4)", color: C.inkOn }}
              onClick={() => { api.logout(); setView("landing"); }}
            >
              Sortir
            </Btn>
          </div>
        </div>
      </div>

      {/* ─── Onglets ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 30 }}>
        <div className="tabs" style={{ maxWidth: 720, margin: "0 auto", borderBottom: "none", padding: "0 8px" }}>
          {TABS.map(t => (
            <button
              key={t.k}
              className={`tab ${tab === t.k ? "active" : ""}`}
              onClick={() => setTab(t.k)}
              aria-current={tab === t.k ? "page" : undefined}
              style={TAP}
            >
              <Icon name={t.icon} size={15} strokeWidth={tab === t.k ? 2 : 1.5} />
              {t.label}
              <span style={{
                ...numeric, fontSize: F.size.xs, fontWeight: F.weight.semi,
                background: tab === t.k ? C.navySoft : C.surfaceAlt,
                color: tab === t.k ? C.navy : C.inkMuted,
                border: `1px solid ${C.border}`, borderRadius: R.pill, padding: "0 6px",
              }}>{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
        {/* ─── Recherche ─────────────────────────────────────────────────── */}
        <div style={{ position: "relative", marginBottom: 12 }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
            <Icon name="search" size={16} />
          </span>
          <input
            className="field"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher client, téléphone, commande…"
            aria-label="Rechercher une commande"
            style={{ ...TAP, padding: "11px 12px 11px 36px", fontSize: F.size.md }}
          />
        </div>

        {/* ─── Chiffres du jour ──────────────────────────────────────────── */}
        {myAssignments?.stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
            <Stat label="Collectes"  icon="package"  value={myAssignments.stats.todayCollections} tone="navy" />
            <Stat label="Livraisons" icon="delivery" value={myAssignments.stats.todayDeliveries} tone="success" />
            <Stat label="Terminées"  icon="check"    value={myAssignments.stats.completedToday}  tone="accent" />
          </div>
        )}

        {/* ─── Commandes assignées ───────────────────────────────────────── */}
        {myAssignments?.orders?.length > 0 && tab === "pending" && (
          <Callout tone="info" icon="list" style={{ marginBottom: 12 }}>
            <strong style={{ color: C.ink, ...numeric }}>{myAssignments.orders.filter(o => o.status === "En attente").length}</strong> collecte(s) assignée(s)
            <span style={{ color: C.inkMuted }}> · <span style={numeric}>{myAssignments.orders.filter(o => o.status === "Prêt").length}</span> livraison(s)</span>
          </Callout>
        )}

        {/* ─── Optimisation de tournée ───────────────────────────────────── */}
        {tab === "pending" && pending.filter(o => !o.collector_status && o.latitude && o.longitude).length >= 2 && (
          <div style={{ marginBottom: 12 }}>
            <Btn icon="map" fullWidth onClick={optimizeRoute} style={{ minHeight: 46 }}>
              Optimiser ma tournée ({pending.filter(o => !o.collector_status && o.latitude && o.longitude).length} arrêts)
            </Btn>
          </div>
        )}

        {/* ─── Résultat de la tournée optimisée ──────────────────────────── */}
        {optimizedRoute && (
          <Modal
            title="Tournée optimisée"
            icon="map"
            width={480}
            onClose={() => setOptimizedRoute(null)}
            footer={
              <>
                <Btn variant="ghost" onClick={() => setOptimizedRoute(null)} style={TAP}>Fermer</Btn>
                <Btn icon="navigate" onClick={() => { openOptimizedMaps(); }} style={TAP}>Ouvrir dans Google Maps</Btn>
              </>
            }
          >
            <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 12 }}>
              Itinéraire calculé depuis votre position actuelle.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {optimizedRoute.stops.map((stop, i) => (
                <div key={stop.id} style={{
                  display: "flex", gap: 12, alignItems: "flex-start",
                  padding: 12, background: C.surfaceAlt, borderRadius: R.md, border: `1px solid ${C.border}`,
                }}>
                  <div style={{
                    background: C.navy, color: C.inkOn, borderRadius: R.md,
                    width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: F.weight.semi, fontSize: F.size.base, flexShrink: 0, ...numeric,
                  }}>{i + 1}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>{stop.client}</div>
                    <div className="icon-text" style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 2 }}>
                      <Icon name="location" size={13} />{stop.address}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        )}

        {loading ? <Spinner /> : error ? (
          <Callout tone="danger" title="Chargement impossible">{error}</Callout>
        ) : tab === "planning" ? (
          /* ─── Planning du jour ────────────────────────────────────────── */
          <div>
            <Callout tone="neutral" icon="calendar" style={{ marginBottom: 12 }}>
              <strong style={{ color: C.ink }}>Planning du jour</strong>
              <span style={{ color: C.inkMuted }}> — {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
            </Callout>
            {displayed.length === 0 ? (
              <Card padded={false}>
                <EmptyState icon="calendar" title="Aucune collecte prévue aujourd'hui" />
              </Card>
            ) : displayed.map(order => (
              <Card key={order.id} className="animate-fadeInUp" style={{ marginBottom: 10, borderLeft: `3px solid ${statusStyle(order.status).dot}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div style={{
                      background: C.navySoft, color: C.navy, borderRadius: R.md, padding: "5px 9px",
                      fontWeight: F.weight.semi, fontSize: F.size.md, flexShrink: 0, ...numeric,
                    }}>
                      {order.time || "N/A"}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="truncate" style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>{order.client}</div>
                      <Ref style={{ fontSize: F.size.sm, color: C.inkMuted }}>{order.id}</Ref>
                    </div>
                  </div>
                  <Badge status={order.status} />
                </div>

                <div className="icon-text" style={{ fontSize: F.size.base, color: C.inkBody, marginBottom: 10, alignItems: "flex-start" }}>
                  <Icon name="location" size={14} color={C.inkMuted} style={{ marginTop: 2 }} />
                  <span>{order.address.substring(0, 50)}{order.address.length > 50 ? "…" : ""}</span>
                </div>

                {trackingOrderId === order.id && gpsBanner("GPS actif — position partagée avec le client")}

                {order.collector_status === "en_route" && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                    padding: "8px 11px", background: C.warningSoft, borderRadius: R.md, border: `1px solid ${C.warningBorder}`,
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.warning, flexShrink: 0, animation: "pulse 1.6s ease infinite" }} />
                    <Icon name="collect" size={14} color={C.warning} />
                    <span style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.warning }}>En route vers le client</span>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {order.status === "En attente" && !order.collector_status && (
                    <Btn variant="success" icon="collect" style={TAP} onClick={() => goEnRoute(order)}>J'y vais</Btn>
                  )}
                  {order.status === "En attente" && order.collector_status === "en_route" && (
                    <>
                      <Btn variant="success" icon="check" style={TAP} onClick={() => confirmCollect(order)}>Je suis arrivé</Btn>
                      <Btn variant="ghost" icon="undo" style={TAP} onClick={() => cancelEnRoute(order)}>Annuler</Btn>
                    </>
                  )}
                  {(order.status === "En attente" && order.collector_status === "arrived") && (
                    <Btn icon="list" style={TAP} onClick={() => { stopGPSTracking(); setCurrentOrder(order); setView("col_form"); }}>Renseigner la facture</Btn>
                  )}
                  {order.status === "Collecté" && !order.items && (
                    <Btn icon="list" style={TAP} onClick={() => { stopGPSTracking(); setCurrentOrder(order); setView("col_form"); }}>Renseigner</Btn>
                  )}
                  {order.status === "Prêt" && order.delivery_date && (
                    <Btn variant="success" icon="delivery" style={TAP} onClick={async () => {
                      if (!window.confirm(`Confirmer la livraison de ${order.id} ?`)) return;
                      try { await api.updateOrder(order.id, { status: "Livré" }); load(); } catch (err) { alert(err.message); }
                    }}>Livré</Btn>
                  )}
                  {order.status === "Prêt" && (
                    <Btn variant="white" icon="phone" style={TAP} onClick={() => window.location.href = `tel:+${formatPhone(order.phone)}`}>Appeler</Btn>
                  )}
                  {(order.collector_status === "en_route" || trackingOrderId === order.id) && (
                    <Btn variant="danger" icon="stop" style={TAP} onClick={() => stopGPSTracking()}>Arrêter le GPS</Btn>
                  )}
                  {order.address && (
                    <Btn variant="white" icon="navigate" style={TAP} onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(order.address)}&dirflg=d`, "_blank")}>
                      Naviguer
                    </Btn>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <Card padded={false} style={{ marginTop: 8 }}>
            <EmptyState
              icon={tab === "delivery" ? "delivery" : "inbox"}
              title={emptyLabel}
              action={tab === "pending" ? <Btn icon="add" style={TAP} onClick={openForm}>Créer une commande</Btn> : undefined}
            />
          </Card>
        ) : displayed.map(order => (
          /* ─── Carte de commande ─────────────────────────────────────────── */
          <Card key={order.id} className="animate-fadeInUp" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
              <div style={{ minWidth: 0 }}>
                <Ref style={{ fontSize: F.size.lg, fontWeight: F.weight.semi }}>{order.id}</Ref>
                <div className="truncate" style={{ fontSize: F.size.md, color: C.inkBody, marginTop: 2 }}>
                  {order.client} · <span style={numeric}>{order.phone}</span>
                </div>
              </div>
              <Badge status={order.status} />
            </div>

            <div style={{
              display: "flex", flexDirection: "column", gap: 6, marginBottom: 12,
              fontSize: F.size.base, color: C.inkBody,
            }}>
              <div className="icon-text" style={{ alignItems: "flex-start" }}>
                <Icon name="location" size={14} color={C.inkMuted} style={{ marginTop: 2 }} />
                <span>{order.address.substring(0, 60)}{order.address.length > 60 ? "…" : ""}</span>
              </div>
              <div className="icon-text">
                <Icon name="calendar" size={14} color={C.inkMuted} />
                <span style={numeric}>{order.date} à {order.time}</span>
              </div>
              {order.delivery_date && order.delivery_time && (
                <div className="icon-text">
                  <Icon name="delivery" size={14} color={C.inkMuted} />
                  <span>Livraison : <strong style={{ color: C.ink, ...numeric }}>{order.delivery_date} — {order.delivery_time}</strong></span>
                </div>
              )}
              {order.assigned_collector_name && (
                <div className="icon-text" style={{ color: C.navy }}>
                  <Icon name="user" size={14} />
                  <span>Collecteur : <strong>{order.assigned_collector_name}</strong></span>
                </div>
              )}
              {order.assigned_delivery_name && (
                <div className="icon-text" style={{ color: C.success }}>
                  <Icon name="delivery" size={14} />
                  <span>Livreur : <strong>{order.assigned_delivery_name}</strong></span>
                </div>
              )}
            </div>

            {order.address && (
              <div
                onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(order.address)}`, "_blank")}
                style={{
                  marginBottom: 12, borderRadius: R.md, overflow: "hidden", cursor: "pointer",
                  border: `1px solid ${C.border}`, position: "relative", height: 130,
                  background: C.surfaceAlt,
                }}
              >
                <iframe
                  title={`map-${order.id}`}
                  width="100%" height="130"
                  style={{ border: "none", pointerEvents: "none", display: "block" }}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(order.address)}&output=embed&z=15`}
                />
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  background: "rgba(10,37,64,.82)",
                  color: C.inkOn, fontSize: F.size.sm, fontWeight: F.weight.medium, padding: "8px 10px",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <Icon name="map" size={14} />
                  <span>Appuyer pour naviguer</span>
                </div>
              </div>
            )}

            {trackingOrderId === order.id && gpsBanner("GPS actif — le client peut suivre votre position")}

            {/* Informations de livraison */}
            {tab === "delivery" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                {order.delivery_date && order.delivery_time ? (
                  <Callout tone="success" icon="calendar">
                    <strong style={{ color: C.ink }}>Livraison prévue :</strong> <span style={numeric}>{order.delivery_date} — {order.delivery_time}</span>
                  </Callout>
                ) : (
                  <Callout tone="warning" icon="pending">
                    Le client n'a pas encore choisi son créneau
                  </Callout>
                )}

                {/* État de facturation */}
                <Callout tone={order.items && order.price ? "success" : "danger"} icon={order.items && order.price ? "receipt" : "alert"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ flex: 1, minWidth: 140 }}>
                      {order.items && order.price
                        ? <>Facturé : <strong style={{ color: C.ink, ...numeric }}>{Number(order.price).toLocaleString()} FCFA</strong></>
                        : "Non facturé — pas de reçu"
                      }
                    </span>
                    {order.items && order.price && (
                      <Btn small variant="white" icon="receipt" onClick={() => setPrintSizeModal(order)}>Reçu</Btn>
                    )}
                  </div>
                </Callout>

                {/* Rappel d'encaissement */}
                {order.payment_status && order.payment_status !== "paye" && order.price && (
                  <Callout tone={order.payment_status === "avance" ? "warning" : "danger"} icon="cash">
                    {order.payment_status === "avance"
                      ? <>Reste à encaisser : <strong style={{ color: C.ink, ...numeric }}>{(Number(order.price) - Number(order.amount_paid || 0)).toLocaleString()} FCFA</strong></>
                      : <>À encaisser : <strong style={{ color: C.ink, ...numeric }}>{Number(order.price).toLocaleString()} FCFA</strong></>
                    }
                  </Callout>
                )}
                {order.payment_status === "paye" && order.price && (
                  <Callout tone="success" icon="success">
                    Payé : <strong style={{ color: C.ink, ...numeric }}>{Number(order.price).toLocaleString()} FCFA</strong>
                  </Callout>
                )}
              </div>
            )}

            {/* Mes collectes */}
            {tab === "mycol" && (
              <div style={{ marginBottom: 10 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  padding: "9px 11px", background: C.surfaceAlt, borderRadius: R.md, border: `1px solid ${C.border}`,
                }}>
                  <Badge status={order.status} />
                  {order.items && (
                    <Chip icon="order" small>{order.items.split(",").filter(Boolean).length} article(s)</Chip>
                  )}
                  {order.items && order.price && (
                    <span style={{ fontWeight: F.weight.semi, color: C.ink, marginLeft: "auto", fontSize: F.size.md, ...numeric }}>
                      {Number(order.price).toLocaleString()} FCFA
                    </span>
                  )}
                </div>

                {order.items && order.price && (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <Btn variant="white" icon="download" style={TAP} onClick={() => setPrintSizeModal(order)}>Reçu PDF</Btn>
                    <Btn variant="white" icon="show" style={TAP} onClick={() => { setCurrentOrder(order); setView("col_recu"); }}>Voir le reçu</Btn>
                    <Btn variant="success" icon="chat" style={TAP} onClick={() => {
                      sendReceiptViaWhatsApp(order, () => {}, (err) => { if (err) alert("Erreur : " + err); });
                    }}>Envoyer</Btn>
                  </div>
                )}
              </div>
            )}

            {/* ─── Actions ─────────────────────────────────────────────────── */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {tab === "pending" && !order.collector_status && (
                <Btn variant="success" icon="collect" style={TAP} onClick={() => goEnRoute(order)}>J'y vais</Btn>
              )}
              {order.status === "En attente" && order.collector_status === "en_route" && (
                <>
                  <Btn variant="success" icon="check" style={TAP} onClick={() => confirmCollect(order)}>Arrivé / Collecté</Btn>
                  <Btn variant="ghost" icon="undo" style={TAP} onClick={() => cancelEnRoute(order)}>Annuler</Btn>
                </>
              )}
              {tab === "collected" && (
                <Btn icon="list" style={TAP} onClick={() => { stopGPSTracking(); setCurrentOrder(order); setView("col_form"); }}>Renseigner</Btn>
              )}
              {tab === "collected" && trackingOrderId === order.id && (
                <Btn variant="danger" icon="stop" style={TAP} onClick={() => stopGPSTracking()}>Arrêter le GPS</Btn>
              )}
              {tab === "delivery" && order.delivery_date && (
                <Btn variant="success" icon="delivery" style={TAP} onClick={async () => {
                  if (!window.confirm(`Confirmer la livraison de ${order.id} à ${order.client} ?`)) return;
                  try {
                    await api.updateOrder(order.id, { status: "Livré" });
                    load();
                    // Consentement WhatsApp intelligent
                    const ord = { ...order, status: "Livré" };
                    if (shouldAskWaConsent(order.phone)) {
                      setWaModal({ order: ord, message: waMessage(ord) });
                    } else {
                      sendWa(order.phone, waMessage(ord));
                      markWaSent(order.phone);
                    }
                  } catch (err) { alert(err.message); }
                }}>Livré</Btn>
              )}

              {/* Appel & WhatsApp — toujours visibles */}
              <Btn variant="white" icon="phone" style={TAP} onClick={() => window.location.href = `tel:+${formatPhone(order.phone)}`}>Appeler</Btn>
              <Btn variant="white" icon="chat" style={TAP} onClick={() => {
                // Messages d'arrivée différenciés collecte vs livraison
                const isSurPlace = order.order_type === "sur_place";
                // Messages WhatsApp SORTANTS (vers le client) : les emojis sont
                // volontairement conservés — c'est du texte envoyé, pas de l'UI,
                // et un message sans emoji paraît froid. Cf. utils.js/waMessage().
                const msgs = {
                  pending: isSurPlace
                    ? `Bonjour *${order.client}* 👋\nNous sommes prêts à réceptionner votre linge en boutique — *${order.id}*.\n— _Setal Express_`
                    : `Bonjour *${order.client}* 👋\nVotre collecteur est en route pour récupérer votre linge 🛵 — *${order.id}*.\nÀ tout de suite !\n— _Setal Express_`,
                  enroute: `Bonjour *${order.client}* 👋\nVotre collecteur arrive chez vous dans quelques minutes 🛵 — *${order.id}*.\n— _Setal Express_`,
                  delivery: order.delivery_date
                    ? `Bonjour *${order.client}* 👋\nNous arrivons pour la livraison de votre commande *${order.id}* 🚚.\nÀ tout de suite !\n— _Setal Express_`
                    : `Bonjour *${order.client}* 👋\nVotre commande *${order.id}* est prête ✨.\nMerci de choisir votre créneau de livraison.\n— _Setal Express_`,
                };
                const msg = msgs[tab] || `Bonjour *${order.client}*, mise à jour sur votre commande *${order.id}* — _Setal Express_`;
                window.open(`https://wa.me/${formatPhone(order.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
              }}>WhatsApp</Btn>

              {(tab === "pending" || tab === "enroute" || (order.status === "En attente" && order.collector_status === "en_route")) && (
                <Btn variant="white" icon="location" style={TAP} onClick={() => {
                  setEditAddrOrder(order);
                  setEditAddrValue(order.address || "");
                  setEditAddrLat(order.latitude ? Number(order.latitude) : 14.7167);
                  setEditAddrLng(order.longitude ? Number(order.longitude) : -17.4677);
                }}>Modifier l'adresse</Btn>
              )}
              {(order.address || order.latitude) && (
                <Btn variant="white" icon="navigate" style={TAP} onClick={() => {
                  const dest = order.latitude && order.longitude
                    ? `${order.latitude},${order.longitude}`
                    : encodeURIComponent(order.address);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`, "_blank");
                }}>Itinéraire</Btn>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* ─── Modale : préciser la localisation ───────────────────────────── */}
      {editAddrOrder && (
        <Modal
          title="Préciser la localisation"
          icon="location"
          width={520}
          onClose={() => setEditAddrOrder(null)}
          footer={
            <>
              <Btn variant="ghost" style={TAP} onClick={() => setEditAddrOrder(null)}>Annuler</Btn>
              <Btn variant="success" icon="save" style={TAP} disabled={editAddrLoading || (!editAddrValue.trim() && !editAddrLat)} onClick={async () => {
                try {
                  const update = { address: editAddrValue.trim() };
                  if (editAddrLat && editAddrLng) { update.latitude = editAddrLat; update.longitude = editAddrLng; }
                  await api.updateOrder(editAddrOrder.id, update);
                  setEditAddrOrder(null);
                  load();
                } catch (err) { alert(err.message); }
              }}>Enregistrer</Btn>
            </>
          }
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 12 }}>
            Commande <Ref style={{ fontSize: F.size.base }}>{editAddrOrder.id}</Ref> — {editAddrOrder.client}
          </p>

          {/* Position GPS immédiate */}
          <div style={{ marginBottom: 12 }}>
            <Btn
              variant="white" icon="track" fullWidth style={{ minHeight: 46 }}
              loading={editAddrLoading}
              onClick={() => {
                setEditAddrLoading(true);
                navigator.geolocation.getCurrentPosition(
                  async pos => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    setEditAddrLat(lat);
                    setEditAddrLng(lng);
                    try {
                      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`);
                      const d = await r.json();
                      const a = d.address || {};
                      const parts = [a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village].filter(Boolean);
                      setEditAddrValue(parts.length ? parts.join(", ") : d.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                    } catch { setEditAddrValue(`${lat.toFixed(6)}, ${lng.toFixed(6)}`); }
                    setEditAddrLoading(false);
                  },
                  () => { alert("Impossible d'obtenir la position GPS"); setEditAddrLoading(false); },
                  { enableHighAccuracy: true, timeout: 15000 }
                );
              }}
            >
              {editAddrLoading ? "Localisation…" : "Ma position actuelle"}
            </Btn>
          </div>

          {/* Carte interactive avec marqueur déplaçable */}
          <div style={{ borderRadius: R.md, overflow: "hidden", border: `1px solid ${C.border}`, marginBottom: 8, height: 260 }}>
            {editAddrLat && editAddrLng && (
              <MapContainer
                center={[editAddrLat, editAddrLng]}
                zoom={16}
                style={{ height: "100%", width: "100%" }}
                key={`${editAddrOrder.id}-${editAddrLat.toFixed(4)}-${editAddrLng.toFixed(4)}`}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                <DraggableMarker lat={editAddrLat} lng={editAddrLng} onMove={(lat, lng) => {
                  setEditAddrLat(lat);
                  setEditAddrLng(lng);
                  // Reverse geocode the new position
                  fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`)
                    .then(r => r.json())
                    .then(d => {
                      const a = d.address || {};
                      const parts = [a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village].filter(Boolean);
                      setEditAddrValue(parts.length ? parts.join(", ") : d.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                    })
                    .catch(() => setEditAddrValue(`${lat.toFixed(6)}, ${lng.toFixed(6)}`));
                }} />
              </MapContainer>
            )}
          </div>

          <div className="icon-text" style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 12 }}>
            <Icon name="hand" size={13} />
            Déplacez le marqueur pour ajuster la position exacte
          </div>

          {/* Adresse texte */}
          <Input
            label="Adresse"
            value={editAddrValue}
            onChange={setEditAddrValue}
            placeholder="Quartier, rue, repère…"
            style={{ marginBottom: 0 }}
          />

          {/* Coordonnées GPS */}
          {editAddrLat && editAddrLng && (
            <div className="icon-text" style={{
              marginTop: 8, padding: "7px 10px", background: C.successSoft,
              border: `1px solid ${C.successBorder}`, borderRadius: R.md,
              fontSize: F.size.sm, color: C.success,
            }}>
              <Icon name="target" size={13} />
              <span style={numeric}>GPS : {editAddrLat.toFixed(6)}, {editAddrLng.toFixed(6)}</span>
            </div>
          )}
        </Modal>
      )}

      {/* ─── Modale : nouvelle commande ──────────────────────────────────── */}
      {showForm && (
        <Modal
          title={newOrder ? "Commande créée" : "Nouvelle commande"}
          icon={newOrder ? "success" : "order"}
          width={560}
          onClose={() => setShowForm(false)}
        >
          {!newOrder ? (
            <form onSubmit={handleCreateOrder}>
              <Input label="Nom du client" required value={fClient} onChange={setFClient} placeholder="ex: Amadou Diallo" />
              <Input label="Téléphone" required value={fPhone} onChange={setFPhone} type="tel" inputMode="tel" placeholder="ex: 77 123 45 67" onBlur={lookupClient} />

              {clientInfo && clientInfo.totalOrders > 0 && (
                <Callout tone="success" icon="star" style={{ marginBottom: 14 }}>
                  Client fidèle · <strong style={{ color: C.ink, ...numeric }}>{clientInfo.totalOrders}</strong> commandes
                </Callout>
              )}

              <div style={{ marginBottom: 14 }}>
                <label htmlFor="col-address" style={{
                  display: "flex", alignItems: "center", gap: 5, fontWeight: F.weight.medium,
                  marginBottom: 5, fontSize: F.size.base, color: C.inkBody,
                }}>
                  Adresse<span style={{ color: C.danger }} aria-hidden="true">*</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="col-address"
                    className="field"
                    value={fAddress} onChange={e => setFAddress(e.target.value)}
                    placeholder="Adresse de collecte" required
                    style={{ ...TAP, flex: 1, fontSize: F.size.md }}
                  />
                  <Btn
                    variant="white" icon="track" title="Utiliser ma position GPS"
                    onClick={getGPS} disabled={gpsLoading} loading={gpsLoading}
                    style={{ ...TAP, flexShrink: 0 }}
                  >
                    GPS
                  </Btn>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input label="Date" required value={fDate} onChange={setFDate} type="date" min={today} />
                <Input label="Heure" required value={fTime} onChange={setFTime} type="time" />
              </div>

              <Textarea label="Notes" value={fNotes} onChange={setFNotes} placeholder="Instructions particulières…" rows={2} />

              {fError && <Callout tone="danger" style={{ marginBottom: 12 }}>{fError}</Callout>}

              <div style={{ display: "flex", gap: 10 }}>
                <Btn type="submit" icon="check" style={{ ...TAP, flex: 1 }} disabled={fSaving} loading={fSaving}>
                  {fSaving ? "Création…" : "Créer la commande"}
                </Btn>
                <Btn variant="ghost" style={TAP} onClick={() => setShowForm(false)}>Annuler</Btn>
              </div>
            </form>
          ) : (
            <div>
              <Callout tone="success" icon="success" title="Commande enregistrée" style={{ marginBottom: 14 }}>
                La commande a bien été créée.
              </Callout>

              <div style={{
                background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
                padding: 16, marginBottom: 16,
              }}>
                <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 4 }}>Numéro de commande</p>
                <Ref style={{ fontSize: F.size["3xl"], fontWeight: F.weight.semi }}>{newOrder.id}</Ref>
                <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 6 }}>
                  {newOrder.client} · <span style={numeric}>{newOrder.phone}</span>
                </p>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Btn icon="list" style={{ ...TAP, flex: 1 }} onClick={() => { setShowForm(false); setTab("pending"); }}>Voir les demandes</Btn>
                <Btn variant="ghost" icon="add" style={TAP} onClick={() => { setNewOrder(null); openForm(); }}>Autre commande</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* ─── Modale : taille d'impression ────────────────────────────────── */}
      {printSizeModal && (
        <Modal
          title="Taille d'impression"
          icon="print"
          width={380}
          onClose={() => setPrintSizeModal(null)}
          footer={<Btn variant="ghost" style={TAP} onClick={() => setPrintSizeModal(null)}>Annuler</Btn>}
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 14 }}>
            Choisissez la taille adaptée à votre imprimante.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn
              variant="white" icon="document" style={{ minHeight: 52, flex: 1 }}
              onClick={async () => {
                const o = printSizeModal; setPrintSizeModal(null);
                try { const doc = await generateReceiptPDF(o, "a4", "normal"); doc.save(`Recu_${o.id}.pdf`); } catch (err) { alert("Erreur PDF"); }
              }}
            >Normal</Btn>
            <Btn
              icon="document" style={{ minHeight: 52, flex: 1 }}
              onClick={async () => {
                const o = printSizeModal; setPrintSizeModal(null);
                try { const doc = await generateReceiptPDF(o, "a4", "grand"); doc.save(`Recu_${o.id}.pdf`); } catch (err) { alert("Erreur PDF"); }
              }}
            >Grand</Btn>
          </div>
        </Modal>
      )}

      {/* ─── Modale : consentement WhatsApp ──────────────────────────────── */}
      {waModal && (
        <Modal
          title="Notifier le client ?"
          icon="chat"
          width={400}
          footer={
            <>
              <Btn variant="ghost" style={TAP} onClick={() => { markWaDeclined(waModal.order.phone); setWaModal(null); }}>Plus tard</Btn>
              <Btn variant="success" icon="send" style={TAP} onClick={() => {
                sendWa(waModal.order.phone, waModal.message);
                markWaSent(waModal.order.phone);
                setWaModal(null);
              }}>Envoyer WhatsApp</Btn>
            </>
          }
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: F.size.base, color: C.inkBody }}>Commande</span>
            <Ref style={{ fontSize: F.size.base }}>{waModal.order.id}</Ref>
            {waModal.order.order_type === "sur_place"
              ? <Chip tone="warning" icon="store" small>Sur place</Chip>
              : <Chip tone="navy" icon="scooter" small>Collecte</Chip>
            }
          </div>
          <div style={{
            background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
            padding: 12, fontSize: F.size.base, color: C.inkBody, lineHeight: 1.6, whiteSpace: "pre-wrap",
          }}>
            {waModal.message}
          </div>
        </Modal>
      )}
    </div>
  );
}

export default ColList;
